import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/api/client';
import { formatLocalTime } from '@/lib/format';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
} from '@/components/ui/dialog';
import { CheckCircle2, XCircle, Clock } from 'lucide-react';

type UnlockDialogProps = {
  accessCode: string;
  bookingStartTime: string;    // ISO UTC datetime
  startTimeLocal: string;      // "HH:mm" for display
  unlockAvailableAt?: string;  // ISO UTC timestamp from backend
  unitName?: string;           // for success message
};

type UnlockApiResponse = {
  success: boolean;
  message: string;
};

type ModalState =
  | { type: 'closed' }
  | { type: 'too_early' }
  | { type: 'loading' }
  | { type: 'success'; message: string }
  | { type: 'error'; message: string };

export default function UnlockDialog({ accessCode, bookingStartTime, startTimeLocal, unlockAvailableAt, unitName }: UnlockDialogProps) {
  const { t, i18n } = useTranslation();
  const [state, setState] = useState<ModalState>({ type: 'closed' });

  const handleUnlock = useCallback(() => {
    // Client-side time check using unlockAvailableAt from backend (or fallback to 15 min)
    const unlockAtMs = unlockAvailableAt
      ? new Date(unlockAvailableAt).getTime()
      : new Date(bookingStartTime).getTime() - 15 * 60 * 1000;
    const nowMs = Date.now();

    if (nowMs < unlockAtMs) {
      setState({ type: 'too_early' });
      return;
    }

    // Call the unlock API
    setState({ type: 'loading' });
    apiClient<UnlockApiResponse>(`/unlock/${accessCode}`, {
      method: 'GET',
      skipAuth: true,
    })
      .then((result) => {
        setState(
          result.success
            ? { type: 'success', message: result.message }
            : { type: 'error', message: result.message },
        );
      })
      .catch((err) => {
        setState({
          type: 'error',
          message: err.message || t('booking.unlock_failed'),
        });
      });
  }, [accessCode, bookingStartTime, t]);

  const isOpen = state.type !== 'closed';
  const close = () => setState({ type: 'closed' });

  return (
    <>
      <Button className="mt-2 w-full whitespace-nowrap" size="lg" onClick={handleUnlock}>
        {t('booking.unlock_door')}
      </Button>

      <Dialog open={isOpen} onOpenChange={(open) => !open && close()}>
        <DialogContent className="max-w-sm text-center">
          {state.type === 'too_early' && (
            <div className="space-y-4 py-4">
              <Clock className="mx-auto h-16 w-16 text-primary" />
              <p className="text-muted-foreground">
                {t('booking.unlock_too_early', {
                  time: formatLocalTime(startTimeLocal, i18n.language),
                  // Long-form weekday + year for the user-facing "too-early"
                  // message (booking could be days/months out, year matters).
                  // Pass i18n.language so the French sentence wrapping this
                  // doesn't get an English-formatted date in the middle.
                  // formatDate ships without `year`, hence the inline format
                  // call rather than the helper.
                  date: new Date(bookingStartTime).toLocaleDateString(
                    i18n.language.startsWith('fr') ? 'fr-CA' : 'en-CA',
                    {
                      weekday: 'long',
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric',
                    },
                  ),
                })}
              </p>
            </div>
          )}

          {state.type === 'loading' && (
            <div className="space-y-4 py-8">
              <div className="mx-auto h-16 w-16 animate-spin rounded-full border-4 border-primary border-t-transparent" />
              <p className="text-lg font-medium">{t('booking.unlocking')}</p>
            </div>
          )}

          {state.type === 'success' && (
            <div className="space-y-4 py-4">
              <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-green-100">
                <CheckCircle2 className="h-12 w-12 text-green-500" />
              </div>
              <h2 className="text-xl font-bold">{t('booking.door_unlocked')}</h2>
              <p className="text-muted-foreground">
                {unitName
                  ? t('booking.door_unlocked_message', { unit: unitName })
                  : state.message}
              </p>
            </div>
          )}

          {state.type === 'error' && (
            <div className="space-y-4 py-4">
              <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-red-100">
                <XCircle className="h-12 w-12 text-red-500" />
              </div>
              <h2 className="text-xl font-bold">{t('booking.unlock_failed')}</h2>
              <p className="text-muted-foreground">
                {t('booking.unlock_error_' + (state.message.includes('early') ? 'early'
                  : state.message.includes('ended') ? 'ended'
                  : 'generic'), { defaultValue: state.message })}
              </p>
              <p className="text-sm text-muted-foreground">{t('booking.unlock_fallback')}</p>
            </div>
          )}

          <DialogFooter className="justify-center">
            <Button onClick={close}>
              {state.type === 'too_early' ? t('booking.unlock_got_it') : t('booking.unlock_close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
